'use client';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { ProductsGrid } from '@eliadmin/core/src/components/product/products-grid';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { Heading } from '../shared/heading';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { TfiBrushAlt } from 'react-icons/tfi';
import { FavoriteProductsDeleteAllDialog } from './favorite-products-delete-all-dialog';
import { DialogProvider, useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';

export const FavoriteProductsContainerInternal = () => {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const { me, isInitialized } = useCustomerStore();

  const [storedFavorites, setStoredFavorites] = useState<number[]>([]);

  const openDialog = useDialogStore(s => s.open);

  useEffect(() => {
    const loadFavoritesFromLocalStorage = () => {
      const storedFavorites = JSON.parse(localStorage?.getItem('favoriteProducts') || '[]')
        .map(Number)
        .filter(Boolean);
      setStoredFavorites(storedFavorites);
    };

    loadFavoritesFromLocalStorage();

    // Function to update the storedFavorites if localStorage changes
    const handleStorageChange = () => {
      loadFavoritesFromLocalStorage();
    };

    window.addEventListener('storage', handleStorageChange);

    // Cleanup: Remove event listener on unmount
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    const updateFavorites = () => {
      const updatedFavorites = JSON.parse(localStorage?.getItem('favoriteProducts') || '[]')
        .map(Number)
        .filter(Boolean);

      // Only update if the current storedFavorites and localStorage differ
      if (JSON.stringify(storedFavorites) !== JSON.stringify(updatedFavorites)) {
        setStoredFavorites(updatedFavorites);
      }
    };

    updateFavorites();
  }, [storedFavorites]);

  const { data: favoriteProducts, isLoading: isLoadingFavoriteProducts } = useQuery({
    queryKey: [CacheKey.FAVORITE_PRODUCTS, locale, storedFavorites, me?.customer.id ?? null],
    queryFn: () =>
      fetchProducts({
        locale,
        authToken: me?.authToken,
        query: { where: { id: { in: storedFavorites } }, results: 9999 },
      }),
    enabled: isInitialized && storedFavorites.length > 0,
  });

  //   console.log(storedFavorites);

  return (
    <>
      <section className={cn('custom-section', 'mt-8 sm:mt-16')}>
        <div className={cn('custom-container', 'flex flex-col items-start justify-start gap-16')}>
          <div className="flex w-full items-center justify-between">
            <Heading title="<strong>WISHLIST</strong>" />
            <Button
              variant={'outline'}
              className="flex h-[52px] w-[52px] items-center justify-center gap-2 whitespace-nowrap p-0 sm:min-h-12 sm:w-auto sm:px-6 sm:py-2"
              disabled={isLoadingFavoriteProducts || !favoriteProducts?.data.length}
              onClick={() =>
                openDialog({
                  id: FavoriteProductsDeleteAllDialog.id,
                  content: <FavoriteProductsDeleteAllDialog />,
                })
              }
            >
              <TfiBrushAlt size={20} /> <p className="hidden sm:block">{t('text1382')}</p>
            </Button>
          </div>
          {isLoadingFavoriteProducts && (
            <div
              className={cn('flex w-full items-center justify-center py-12 sm:py-16 md:py-20', '')}
            >
              <Spinner className={cn('h-12 w-12 sm:h-16 sm:w-16 md:h-20 md:w-20', '')} />
            </div>
          )}
          {!favoriteProducts?.data.length && !isLoadingFavoriteProducts && (
            <div className="w-full text-center text-lg">{t('text1381')}</div>
          )}
          {!!favoriteProducts?.data.length && !isLoadingFavoriteProducts && (
            <div className="w-full">
              <ProductsGrid products={favoriteProducts.data} />
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export const FavoriteProductsContainer = () => {
  return (
    <DialogProvider>
      <FavoriteProductsContainerInternal />
    </DialogProvider>
  );
};
